import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-surface px-4" data-testid="login-page">
      <div className="w-full max-w-md bg-brand-bg border border-brand-line rounded-2xl p-8 sm:p-10 shadow-sm">
        <div className="w-12 h-12 rounded-full bg-brand-green flex items-center justify-center mb-6">
          <Lock className="w-5 h-5 text-brand-bg" />
        </div>
        <h1 className="font-serif text-3xl text-brand-ink mb-1">Admin Login</h1>
        <p className="text-brand-muted text-sm mb-8">Access the ministry dashboard.</p>

        {error && (
          <div className="bg-destructive/10 text-destructive text-sm rounded-lg px-4 py-3 mb-4" data-testid="login-error">
            {error}
          </div>
        )}

        <form onSubmit={submit} className="space-y-4" data-testid="login-form">
          <input
            required type="email" data-testid="login-email" placeholder="Email address"
            value={email} onChange={(e) => setEmail(e.target.value)}
            className="bg-white border border-brand-line rounded-lg px-4 py-3 focus:ring-2 focus:ring-brand-green outline-none transition-all w-full"
          />
          <input
            required type="password" data-testid="login-password" placeholder="Password"
            value={password} onChange={(e) => setPassword(e.target.value)}
            className="bg-white border border-brand-line rounded-lg px-4 py-3 focus:ring-2 focus:ring-brand-green outline-none transition-all w-full"
          />
          <button
            type="submit" disabled={loading} data-testid="login-submit-btn"
            className="w-full inline-flex items-center justify-center gap-2 bg-brand-green text-brand-bg hover:bg-brand-greendark px-8 py-3.5 rounded-full font-medium transition-all duration-300 disabled:opacity-60"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}
